import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./index.scss";
import PageHeader from "../../../adminCompenents/listComponents/Layout";
import CardContainer from "../../../adminCompenents/listComponents/UI/CardContainer";
import ExportButton from "../../../adminCompenents/listComponents/UI/ExportButton";
import AddButton from "../../../adminCompenents/listComponents/UI/AddButton";
import SearchFilterBar from "../../../adminCompenents/listComponents/UI/SearchFilterBar";
import DataTable from "../../../adminCompenents/listComponents/UI/DataTable";
import Pagination from "../../../adminCompenents/listComponents/UI/Pagination";
import DataCards from "../../../adminCompenents/listComponents/UI/DataCards";
import ProductService from "../../../services/product";
import { showErrorToaster } from "../../../adminCompenents/listComponents/UI/toaster/error";
import { showSuccessToaster } from "../../../adminCompenents/listComponents/UI/toaster/success";
import { exportCSV } from "../../../adminCompenents/utils";

const productService = new ProductService();

const ProductsPage = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const perPage = 5;
  const [filters, setFilters] = useState({});
  const [isMobile, setIsMobile] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  // 👉 Responsive detection
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // 👉 Load products from API
  const loadProducts = async () => {
    setIsLoading(true);
    try {
      const response = await productService.getAllProducts();
      setProducts(
        Array.isArray(response.data) ? response.data : response.data?.data || []
      );
    } catch (error) {
      showErrorToaster("Erreur lors du chargement des produits");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadProducts();
  }, []);

  // 👉 Filter products
  const filtered = products.filter((p) => {
    return (
      p.title?.toLowerCase().includes(search.toLowerCase()) &&
      (filters.category
        ? p.category?.name
            ?.toLowerCase()
            .includes(filters.category.toLowerCase())
        : true) &&
      (filters.brand
        ? p.brand?.toLowerCase().includes(filters.brand.toLowerCase())
        : true)
    );
  });

  const totalPages = Math.ceil(filtered.length / perPage);
  const paginated = filtered.slice((page - 1) * perPage, page * perPage);

  // 👉 Handle delete product
  const handleDelete = async (productId) => {
    if (window.confirm("Êtes-vous sûr de vouloir supprimer ce produit ?")) {
      try {
        await productService.deleteProduct(productId);
        setProducts((prev) => prev.filter((p) => p._id !== productId));
        showSuccessToaster("Produit supprimé avec succès");
      } catch (error) {
        showErrorToaster("Erreur lors de la suppression du produit");
      }
    }
  };

  // 👉 Handle export
  const handleExport = () => {
    const data = products.map((product) => ({
      Titre: product.title,
      Catégorie: product.categoryId?.title || "N/A",
      "Prix HT": product.prixHorsTax,
      "Prix TTC": product.ttc,
      Quantité: product.quantite,
      "En Stock": product.onStock ? "Oui" : "Non",
      Statut: product.status ? "Actif" : "Inactif",
      "Date de création": new Date(product.createdAt).toLocaleDateString(
        "fr-FR"
      ),
    }));
    exportCSV(data, "produits");
  };

  const filterFields = [
    {
      name: "category",
      label: "Catégorie",
      placeholder: "Rechercher par catégorie...",
    },
    { name: "brand", label: "Marque", placeholder: "Rechercher par marque..." },
  ];

  const columns = [
    {
      header: "Produit",
      accessor: "title",
      Cell: (value, row) => (
        <div className="d-flex align-items-center">
          <img
            src={`https://api.almasecret.msit-demo.fr/products/${row?._id}/image/${0}`}
            alt={row.title}
            className="product-img me-2"
            style={{
              width: "40px",
              height: "40px",
              objectFit: "cover",
              borderRadius: "4px",
            }}
          />
          <div>
            <div className="fw-bold">{value}</div>
            <small className="text-muted">{row.reference || "N/A"}</small>
          </div>
        </div>
      ),
    },
    {
      header: "Catégorie",
      accessor: "categoryId",
      Cell: (value) => value?.title || "N/A",
    },
    {
      header: "Prix TTC",
      accessor: "ttc",
      Cell: (value) => `${value}DT` || "N/A",
    },
    {
      header: "Quantité",
      accessor: "quantite",
      Cell: (value) => value || 0,
    },
    {
      header: "Stock",
      accessor: "onStock",
      Cell: (value) => (
        <span className={`badge ${value ? "bg-success" : "bg-danger"}`}>
          {value ? "En Stock" : "Rupture"}
        </span>
      ),
    },
    {
      header: "Statut",
      accessor: "status",
      Cell: (value) => (
        <span className={`badge ${value ? "bg-success" : "bg-secondary"}`}>
          {value ? "Actif" : "Inactif"}
        </span>
      ),
    },
    {
      header: "Date de création",
      accessor: "createdAt",
      Cell: (value) =>
        value ? new Date(value).toLocaleDateString("fr-FR") : "N/A",
    },
  ];

  const rowActions = (row) => [
    {
      label: "Voir",
      onClick: () => navigate(`/details-product/${row._id}`),
    },
    {
      label: "Modifier",
      onClick: () => navigate(`/edit-product/${row._id}`),
    },
    {
      label: "Supprimer",
      onClick: () => handleDelete(row._id),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Produits"
        breadcrumb={[{ label: "Accueil", href: "/" }, { label: "Produits" }]}
        isLoading={isLoading}
      />

      <CardContainer>
        <div className="d-flex justify-content-between align-items-center mb-3 m-4">
          <h5 className="title-liste">Liste des produits</h5>
          <div className="d-flex gap-2">
            <ExportButton onClick={handleExport} />
            <AddButton
              label="Produit"
              onClick={() => navigate("/add-product")}
            />
          </div>
        </div>
        <hr className="hr-table" />
        <SearchFilterBar
          fields={filterFields}
          onSearch={setSearch}
          onFilter={(f) => setFilters(f)}
        />
        <hr className="hr-table" />

        {/* ✅ Responsive affichage */}
        {isMobile ? (
          <DataCards
            columns={columns}
            data={paginated}
            keyField="_id"
            rowActions={rowActions}
          />
        ) : (
          <DataTable
            columns={columns}
            data={paginated}
            keyField="_id"
            rowActions={rowActions}
          />
        )}

        <Pagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={setPage}
        />
      </CardContainer>
    </div>
  );
};

export default ProductsPage;
